<template>
  <nav class="chapter-rail" :class="{ 'rail-static': !sticky }" :aria-label="label">
    <span
      class="chapter-rail-lens"
      v-if="sticky"
      :class="{ ready: lensReady }"
      :style="lensStyle"
      aria-hidden="true"
    ></span>
    <button
      v-for="item in items"
      :key="item.id"
      class="chapter-rail-item"
      :class="{ active: sticky && item.id === activeId }"
      :data-chapter-id="item.id"
      type="button"
      :title="item.title || item.label"
      :aria-current="sticky && item.id === activeId ? 'location' : undefined"
      @click="scrollToChapter(item.id)"
    >
      <span class="chapter-rail-dot" aria-hidden="true"></span>
      <span>{{ item.label }}</span>
    </button>
  </nav>
</template>

<script>
import { settleScrollToId } from '../utils/anchorLanding';

export default {
  name: 'ChapterRail',
  props: {
    items: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      default: 'Page sections'
    },
    // Study and Work keep the bar stuck to the top while scrolling. Research
    // and Builds let it scroll away with the page.
    sticky: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      activeId: this.items[0] ? this.items[0].id : '',
      frame: null,
      lensReady: false,
      lensStyle: {
        opacity: 0
      }
    };
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.lensReady = false;
        this.$nextTick(this.updateActive);
      }
    }
  },
  mounted() {
    this.onScroll = () => {
      if (!this.sticky) return;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateActive();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.$nextTick(this.updateActive);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.updateLens()).catch(() => {});
    }
  },
  unmounted() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  },
  methods: {
    updateActive() {
      if (!this.sticky) return;
      const candidates = this.items
        .map((item) => ({ item, el: document.getElementById(item.id) }))
        .filter(({ el }) => el);
      if (!candidates.length) return;

      const focusLine = Math.min(window.innerHeight * 0.34, 300);
      let current = candidates[0].item.id;
      for (const candidate of candidates) {
        if (candidate.el.getBoundingClientRect().top <= focusLine) {
          current = candidate.item.id;
        }
      }
      if (this.activeId !== current) {
        this.activeId = current;
      }
      this.$nextTick(this.updateLens);
    },
    updateLens() {
      if (!this.sticky || !this.$el) return;
      const buttons = Array.from(this.$el.querySelectorAll('.chapter-rail-item'));
      const active = buttons.find((button) => button.dataset.chapterId === this.activeId);
      if (!active) return;

      this.lensStyle = {
        width: `${active.offsetWidth}px`,
        height: `${active.offsetHeight}px`,
        transform: `translate3d(${active.offsetLeft}px, ${active.offsetTop}px, 0)`,
        opacity: 1
      };

      if (!this.lensReady) {
        requestAnimationFrame(() => {
          if (this.$el) this.lensReady = true;
        });
      }
    },
    scrollToChapter(id) {
      if (this.sticky) {
        this.activeId = id;
        this.$nextTick(this.updateLens);
      }
      // Scroll immediately, then keep the landing true to the scroll-margin
      // spot while late fonts/images are still shifting the layout.
      settleScrollToId(id);
    }
  }
};
</script>

<style scoped>
.chapter-rail {
  position: sticky;
  top: var(--chapter-rail-sticky-top, 0.4rem);
  z-index: 40;
  width: auto;
  margin: 1.35rem 0 0.4rem;
  padding: 0.35rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.15rem;
  overflow-x: auto;
  border: 0;
  border-radius: 0;
  background: rgba(228, 228, 233, 0.4);
  box-shadow: none;
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  scrollbar-width: none;
  font-family: "Quattrocento Sans", sans-serif;
}

.chapter-rail.rail-static {
  position: relative;
  top: auto;
}

.chapter-rail::-webkit-scrollbar {
  display: none;
}

.chapter-rail-lens {
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  box-sizing: border-box;
  border: 0;
  border-radius: 0;
  background: rgba(0, 0, 0, 0.05);
  box-shadow: none;
  pointer-events: none;
  will-change: transform, width, height;
}

.chapter-rail-lens.ready {
  transition:
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    width 260ms cubic-bezier(0.22, 1, 0.36, 1),
    height 260ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 120ms ease;
}

.chapter-rail-item {
  position: relative;
  z-index: 1;
  display: inline-flex;
  box-sizing: border-box;
  width: 0;
  min-width: 0;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 34px;
  padding: 0.2rem 0.55rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #8a8a8a;
  font: inherit;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
  text-align: center;
  cursor: pointer;
  transition: color 150ms ease, background-color 150ms ease, scale 140ms cubic-bezier(0.23, 1, 0.32, 1);
}

.chapter-rail-dot {
  width: 5px;
  height: 5px;
  flex: 0 0 auto;
  border: 1px solid currentColor;
  border-radius: 50%;
  background: transparent;
  transition: background-color 150ms ease, scale 150ms cubic-bezier(0.23, 1, 0.32, 1);
}

.chapter-rail-item.active {
  color: #333;
  background: transparent;
}

.chapter-rail-item.active .chapter-rail-dot {
  background: rgba(0, 0, 0, 0.42);
  border-color: transparent;
  scale: 1.25;
}

.chapter-rail-item:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.chapter-rail-item:active {
  scale: 0.97;
}

:global(body.dark-mode .chapter-rail-item) {
  color: #9c9c9c;
}

:global(body.dark-mode .chapter-rail-item.active) {
  color: #d4d4d4;
  background: transparent;
}

:global(body.dark-mode .chapter-rail-item.active .chapter-rail-dot) {
  background: rgba(255, 255, 255, 0.55);
  border-color: transparent;
}

:global(body.dark-mode .chapter-rail-lens) {
  background: rgba(255, 255, 255, 0.065);
  box-shadow: none;
}

:global(html.theme-transitioning .chapter-rail),
:global(body.theme-transitioning .chapter-rail) {
  transition:
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}

:global(html.theme-transitioning .chapter-rail-item),
:global(body.theme-transitioning .chapter-rail-item) {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease,
    scale 140ms cubic-bezier(0.23, 1, 0.32, 1);
}

:global(html.theme-transitioning .chapter-rail-dot),
:global(body.theme-transitioning .chapter-rail-dot) {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease,
    scale 150ms cubic-bezier(0.23, 1, 0.32, 1);
}

:global(html.theme-transitioning .chapter-rail-lens.ready),
:global(body.theme-transitioning .chapter-rail-lens.ready) {
  transition:
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    width 260ms cubic-bezier(0.22, 1, 0.36, 1),
    height 260ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 120ms ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}

@media (hover: hover) and (pointer: fine) {
  .chapter-rail-item:hover {
    color: #333;
    background: rgba(0, 0, 0, 0.03);
  }

  :global(body.dark-mode .chapter-rail-item:hover) {
    color: #d4d4d4;
    background: rgba(255, 255, 255, 0.055);
  }
}

:global(body.dark-mode .chapter-rail) {
  background: rgba(66, 66, 71, 0.42);
  box-shadow: none;
}

@media (max-width: 520px) {
  .chapter-rail-item {
    gap: 0;
    padding-inline: 0.25rem;
    font-size: clamp(0.62rem, 2.4vw, 0.68rem);
  }

  .chapter-rail-dot {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chapter-rail-item,
  .chapter-rail-dot {
    transition: color 120ms ease, background-color 120ms ease;
  }

  .chapter-rail-lens.ready {
    transition: opacity 120ms ease;
  }

  :global(html.theme-transitioning .chapter-rail-item),
  :global(body.theme-transitioning .chapter-rail-item),
  :global(html.theme-transitioning .chapter-rail-dot),
  :global(body.theme-transitioning .chapter-rail-dot) {
    transition:
      color var(--theme-duration, 220ms) ease,
      background-color var(--theme-duration, 220ms) ease,
      border-color var(--theme-duration, 220ms) ease;
  }

  :global(html.theme-transitioning .chapter-rail-lens.ready),
  :global(body.theme-transitioning .chapter-rail-lens.ready) {
    transition:
      opacity 120ms ease,
      background-color var(--theme-duration, 220ms) ease,
      border-color var(--theme-duration, 220ms) ease;
  }
}
</style>
